import { useState, useEffect } from 'react';  // Corrected import

// Projects Component
function Projects({ darkMode }) {
  const [activeProject, setActiveProject] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const projects = [
    {
      id: 1,
      title: "E-Commerce Platform",
      category: "Full Stack",
      description: "Modern e-commerce solution with React, Node.js, and Stripe integration. Features include real-time inventory, user authentication, and responsive design.",
      image: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%236366f1'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='16' fill='white'%3EE-Commerce Demo%3C/text%3E%3C/svg%3E",
      technologies: ["React", "Node.js", "MongoDB", "Stripe"],
      link: "https://your-ecommerce-demo.com",
      github: "https://github.com/yourusername/ecommerce-project",
      featured: true
    },
    {
      id: 2,
      title: "Task Management App",
      category: "Frontend",
      description: "Collaborative task management tool with drag-and-drop functionality, real-time updates, and team collaboration features.",
      image: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%2310b981'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='16' fill='white'%3ETask Manager%3C/text%3E%3C/svg%3E",
      technologies: ["React", "Redux", "Tailwind", "Firebase"],
      link: "https://your-taskmanager-demo.com",
      github: "https://github.com/yourusername/task-manager",
      featured: false
    },
    {
      id: 3,
      title: "Weather Dashboard",
      category: "API Integration",
      description: "Beautiful weather dashboard with location-based forecasts, interactive maps, and detailed weather analytics.",
      image: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23f59e0b'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='16' fill='white'%3EWeather App%3C/text%3E%3C/svg%3E",
      technologies: ["JavaScript", "OpenWeather API", "Chart.js", "CSS3"],
      link: "https://your-weather-demo.com",
      github: "https://github.com/yourusername/weather-dashboard",
      featured: false
    },
    {
      id: 4,
      title: "Portfolio Website",
      category: "Design & Development",
      description: "Modern, responsive portfolio showcasing creative design and smooth animations. Built with React and advanced CSS techniques.",
      image: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect width='400' height='300' fill='%23ec4899'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='16' fill='white'%3EPortfolio Site%3C/text%3E%3C/svg%3E",
      technologies: ["React", "Tailwind", "Framer Motion", "Vercel"],
      link: "https://your-portfolio-demo.com",
      github: "https://github.com/yourusername/portfolio",
      featured: true
    }
  ];

  // Using useEffect instead of useState for side-effects
  useEffect(() => {
    setIsVisible(true);
  }, []);

  const nextProject = () => {
    setActiveProject((prev) => (prev + 1) % projects.length);
  };

  const prevProject = () => {
    setActiveProject((prev) => (prev - 1 + projects.length) % projects.length);
  };

  return (
    <section id="projects" className={`relative py-20 overflow-hidden transition-all duration-700 ${darkMode ? 'bg-gradient-to-b from-slate-900 via-purple-900 to-slate-800' : 'bg-gradient-to-b from-slate-800 via-purple-800 to-slate-700'}`}>
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-10 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-pulse animation-delay-4000"></div>
      </div>

      {/* Floating code elements */}
      <div className="absolute inset-0 pointer-events-none">
        {['<div>', '</div>', 'const', 'function', 'return', 'export', 'import', 'useState'].map((code, i) => (
          <div
            key={i}
            className="absolute text-white/5 font-mono text-lg animate-float"
            style={{
              left: `${5 + (i * 15)}%`,
              top: `${10 + (i * 10)}%`,
              animationDelay: `${i * 0.8}s`,
              animationDuration: `${6 + Math.random() * 3}s`
            }}
          >
            {code}
          </div>
        ))}
      </div>

      <div className="relative container mx-auto px-6">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 mb-4 tracking-tight">
            My Projects
          </h2>
          <div className="h-1 w-32 bg-gradient-to-r from-cyan-400 to-purple-600 rounded-full mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            A showcase of my recent work, featuring <span className="text-cyan-400 font-semibold">innovative solutions</span> and 
            <span className="text-purple-400 font-semibold"> creative implementations</span> across various technologies.
          </p>
        </div>

        {/* Featured Project Carousel */}
        <div className={`mb-20 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="relative max-w-6xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-800/50 to-slate-700/50 backdrop-blur-sm border border-white/10 shadow-2xl">
               {/* Project Content */}
               <div className="md:flex">
                {/* Project Image */}
                <div className="md:w-1/2 relative group">
                  <img 
                    src={projects[activeProject].image}
                    alt={projects[activeProject].title}
                    className="w-full h-64 md:h-96 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                   {/* Project Category Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 backdrop-blur-sm rounded-full border border-cyan-500/30 text-cyan-400 font-semibold text-sm">
                      {projects[activeProject].category}
                    </span>
                  </div>
                   {/* Featured Badge */}
                  {projects[activeProject].featured && (
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 bg-gradient-to-r from-pink-500/20 to-orange-500/20 backdrop-blur-sm rounded-full border border-pink-500/30 text-pink-400 font-semibold text-sm flex items-center">
                        ⭐ Featured
                      </span>
                    </div>
                  )}
                </div>

                {/* Project Details */}
                <div className="md:w-1/2 p-8 flex flex-col justify-center">
                  <h3 className="text-3xl font-bold text-white mb-4">
                    {projects[activeProject].title}
                  </h3>
                  <p className="text-gray-300 text-lg leading-relaxed mb-6">
                    {projects[activeProject].description}
                  </p>
                   {/* Technologies */}
                  <div className="mb-8">
                    <h4 className="text-white font-semibold mb-3">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {projects[activeProject].technologies.map((tech, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-gray-300 text-sm border border-white/20 hover:border-cyan-400/50 transition-colors duration-300"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                   {/* Action Buttons */}
                  <div className="flex space-x-4">
                    <a 
                      href={projects[activeProject].link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    >
                      <span>Live Demo</span>
                      <svg className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                    <a 
                      href={projects[activeProject].github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center px-6 py-3 border-2 border-gray-600 rounded-full font-semibold text-gray-300 hover:text-white hover:border-cyan-400 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm"
                    >
                      <span>Source Code</span>
                      <svg className="ml-2 w-4 h-4 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                      </svg>
                    </a>
                  </div>
                </div>
              </div>

              {/* Navigation Arrows */}
              <button 
                onClick={prevProject}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/40 transition-all duration-300 border border-white/10 hover:border-cyan-400/50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button 
                onClick={nextProject}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/40 transition-all duration-300 border border-white/10 hover:border-cyan-400/50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Project Indicators */}
            <div className="flex justify-center mt-8 space-x-3">
              {projects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveProject(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${index === activeProject ? 'bg-gradient-to-r from-cyan-400 to-purple-600 w-8' : 'bg-gray-600 hover:bg-gray-500'}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Project Grid */}
        <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-3xl font-bold text-white mb-8 text-center">All Projects</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div 
                key={project.id}
                className="group relative bg-gradient-to-r from-slate-800/50 to-slate-700/50 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 hover:border-cyan-400/30 transition-all duration-500 transform hover:scale-105 hover:shadow-2xl"
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  
                  {project.featured && (
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 bg-gradient-to-r from-pink-500/30 to-orange-500/30 backdrop-blur-sm rounded-full text-pink-400 font-semibold text-xs">
                        ⭐ Featured
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors duration-300">
                      {project.title}
                    </h4>
                    <span className="px-2 py-1 bg-white/10 rounded-full text-xs text-gray-400">
                      {project.category}
                    </span>
                  </div>
                  
                  <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-1 mb-4">
                    {project.technologies.slice(0, 3).map((tech, techIndex) => (
                      <span 
                        key={techIndex}
                        className="px-2 py-1 bg-white/10 rounded text-xs text-gray-300"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > 3 && (
                      <span className="px-2 py-1 bg-white/10 rounded text-xs text-gray-400">
                        +{project.technologies.length - 3}
                      </span>
                    )}
                  </div>

                  <div className="flex space-x-3">
                    <a 
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center py-2 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 backdrop-blur-sm rounded-lg border border-cyan-500/30 text-cyan-400 hover:from-cyan-500/30 hover:to-purple-500/30 transition-all duration-300 text-sm font-semibold"
                    >
                      Live Demo
                    </a>
                    <a 
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 text-center py-2 border border-gray-600 rounded-lg text-gray-300 hover:border-cyan-400 hover:text-white transition-all duration-300 text-sm font-semibold"
                    >
                      Code
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Projects;
